import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, Filter, X, Save, ChevronDown, MapPin, Globe, Mountain, Loader2, Building2 } from 'lucide-react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import type { View } from '../types';
import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';

// Export FilterState interface so it can be imported by other components
export interface FilterState {
  search: string | null;
  status: string[] | null;
  distance: string | null;
  sourceStates: string[];
  sourceCountries: string[];
  sourceContinent: string[];
  destinationStates: string[];
  destinationCountries: string[];
  destinationContinent: string[];
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  category: string[];
  seasons: string[];
  type: string[];
  location?: string;
  exactTypeMatch: boolean;
}

interface SavedFilter {
  id: string;
  name: string;
  filter: FilterState;
  createdAt: Date;
}

interface FilterBarProps {
  onFilter: (filters: FilterState) => void;
  views: View[];
  isLoading?: boolean;
  initialFilters?: FilterState;
  onRandomView?: () => void;
}

const initialFilterState: FilterState = {
  search: null,
  status: ['confirmed'],
  distance: "1000",
  sourceStates: [],
  sourceCountries: [],
  sourceContinent: [],
  destinationStates: [],
  destinationCountries: [],
  destinationContinent: [],
  sortBy: 'distance',
  sortDirection: 'desc',
  category: [],
  seasons: [],
  type: [],
  location: '',
  exactTypeMatch: true
};

export function FilterBar({ onFilter, views, isLoading = false, initialFilters, onRandomView }: FilterBarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [filters, setFilters] = useState<FilterState>(initialFilters || initialFilterState);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [filterName, setFilterName] = useState('');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
  const [availableStatuses] = useState<string[]>(['confirmed', 'likely', 'ifclear', 'plausible', 'dubious', 'unlikely']);
  const availableTypes = useMemo(() => [
    'Bridge',
    'Chimney',
    'Coast',
    'Island',
    'Mountain(s)',
    'Skyline',
    'Water Body'
  ], []);
  const [distanceRange, setDistanceRange] = useState<[number, number]>([0, 1000]);
  const [locationTab, setLocationTab] = useState('source');
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [destSearchTerm, setDestSearchTerm] = useState('');
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [isApplyingFilter, setIsApplyingFilter] = useState(false);
  const isInitialMount = useRef(true);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const filterToggleRef = useRef<HTMLButtonElement>(null);
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const confirmedButtonRef = useRef<HTMLButtonElement>(null);
  const distance1000ButtonRef = useRef<HTMLButtonElement>(null);
  const searchTimer = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile(768);
  const [inputValue, setInputValue] = useState<string>('');
  
  // Add a separate state to track pending filters
  const [pendingFilters, setPendingFilters] = useState<FilterState>(initialFilters || initialFilterState);
  
  // Parse filters from URL parameters
  const parseFiltersFromURL = useCallback((): Partial<FilterState> => {
    const urlFilters: Partial<FilterState> = {};
    
    // Search parameter
    const search = searchParams.get('search');
    if (search) urlFilters.search = search;
    
    // Status parameters (comma-separated)
    const status = searchParams.get('status');
    if (status) urlFilters.status = status.split(',');
    
    // Distance parameter
    const distance = searchParams.get('distance');
    if (distance) urlFilters.distance = distance;
    
    // Source location parameters
    const sourceStates = searchParams.get('sourceStates');
    if (sourceStates) urlFilters.sourceStates = sourceStates.split(',');
    
    const sourceCountries = searchParams.get('sourceCountries');
    if (sourceCountries) urlFilters.sourceCountries = sourceCountries.split(',');
    
    const sourceContinent = searchParams.get('sourceContinent');
    if (sourceContinent) urlFilters.sourceContinent = sourceContinent.split(',');
    
    // Destination location parameters
    const destStates = searchParams.get('destStates');
    if (destStates) urlFilters.destinationStates = destStates.split(',');
    
    const destCountries = searchParams.get('destCountries');
    if (destCountries) urlFilters.destinationCountries = destCountries.split(',');
    
    const destContinent = searchParams.get('destContinent');
    if (destContinent) urlFilters.destinationContinent = destContinent.split(',');
    
    // Type parameter
    const type = searchParams.get('type');
    if (type) urlFilters.type = type.split(',');
    
    // Sort parameters
    const sortBy = searchParams.get('sortBy');
    if (sortBy) urlFilters.sortBy = sortBy;
    
    const sortDirection = searchParams.get('sortDirection');
    if (sortDirection === 'asc' || sortDirection === 'desc') {
      urlFilters.sortDirection = sortDirection;
    }
    
    return urlFilters;
  }, [searchParams]);
  
  // Update URL parameters when filters change
  const updateURLParams = useCallback((newFilters: FilterState) => {
    const params = new URLSearchParams();
    
    // Only add non-default parameters to keep URLs clean
    if (newFilters.search) {
      params.set('search', newFilters.search);
    }
    
    if (newFilters.status && newFilters.status.length > 0 && 
        !(newFilters.status.length === 1 && newFilters.status[0] === 'confirmed')) {
      params.set('status', newFilters.status.join(','));
    }
    
    if (newFilters.distance && newFilters.distance !== '1000') {
      params.set('distance', newFilters.distance);
    }
    
    if (newFilters.sourceStates.length > 0) {
      params.set('sourceStates', newFilters.sourceStates.join(','));
    }
    
    if (newFilters.sourceCountries.length > 0) {
      params.set('sourceCountries', newFilters.sourceCountries.join(','));
    }
    
    if (newFilters.sourceContinent.length > 0) {
      params.set('sourceContinent', newFilters.sourceContinent.join(','));
    }
    
    if (newFilters.destinationStates.length > 0) {
      params.set('destStates', newFilters.destinationStates.join(','));
    }
    
    if (newFilters.destinationCountries.length > 0) {
      params.set('destCountries', newFilters.destinationCountries.join(','));
    }
    
    if (newFilters.destinationContinent.length > 0) {
      params.set('destContinent', newFilters.destinationContinent.join(','));
    }
    
    if (newFilters.type && newFilters.type.length > 0) {
      params.set('type', newFilters.type.join(','));
    }
    
    if (newFilters.sortBy && newFilters.sortBy !== 'distance') {
      params.set('sortBy', newFilters.sortBy);
    }
    
    if (newFilters.sortDirection && newFilters.sortDirection !== 'desc') {
      params.set('sortDirection', newFilters.sortDirection);
    }
    
    // Update URL without triggering navigation
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);
  
  // Initialize filters from URL on mount
  useEffect(() => {
    const urlFilters = parseFiltersFromURL();
    if (Object.keys(urlFilters).length > 0) {
      const mergedFilters = {
        ...initialFilterState,
        ...urlFilters
      };
      setFilters(mergedFilters);
      setPendingFilters(mergedFilters);
      setInputValue(mergedFilters.search || '');
      // Apply the filters from URL
      onFilter(mergedFilters);
    }
  }, []); // Run only on mount
  
  // Initialize input value from filters and sync when filters.search changes
  useEffect(() => {
    setInputValue(filters.search || '');
  }, [filters.search]);
  
  // Only update the local input value without applying filters
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Clear any existing timers
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    
    // Set a new timer to apply the filter with longer delay for smoother UX
    searchTimer.current = setTimeout(() => {
      const value = newValue.trim() === '' ? null : newValue.trim();
      
      // Store the current focused element to restore focus later
      const activeElement = document.activeElement;
      const isSearchInputFocused = activeElement === searchInputRef.current;
      
      // Use a more stable approach that doesn't cause re-renders during typing
      setFilters(currentFilters => {
        // Only update if the search value actually changed
        if (currentFilters.search === value) {
          return currentFilters; // No change, prevent re-render
        }
        
        const newFilters = {...currentFilters, search: value};
        
        // Apply the filter with the updated state
        setIsApplyingFilter(true);
        
        // Use requestAnimationFrame to ensure DOM updates don't interfere with focus
        requestAnimationFrame(() => {
        onFilter(newFilters);
        updateURLParams(newFilters); // Update URL with new filters
        
          // Restore focus to search input if it was focused before
          if (isSearchInputFocused && searchInputRef.current) {
            searchInputRef.current.focus();
            // Restore cursor position
            const cursorPosition = newValue.length;
            searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
          
          // Use a longer timeout for the loading state to prevent UI flickering
          setTimeout(() => setIsApplyingFilter(false), 150);
        });
        
        return newFilters;
      });
      
      // Update pending filters separately to keep them in sync
      setPendingFilters(currentPending => {
        if (currentPending.search === value) {
          return currentPending; // No change, prevent re-render
        }
        return {...currentPending, search: value};
      });
      
      searchTimer.current = null;
    }, 600); // Increased from 300ms to 600ms for smoother UX
  }, [onFilter]);
  
  // Clear search
  const clearSearch = useCallback(() => {
    // Update local input value
    setInputValue('');
    
    // Clear any existing timers
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    
    // Update filters immediately using functional update to avoid stale closure
    setFilters(currentFilters => {
      // Only update if search is not already null
      if (currentFilters.search === null) {
        return currentFilters; // No change, prevent re-render
      }
      
      const newFilters = {...currentFilters, search: null};
      
      // Apply the filter
      setIsApplyingFilter(true);
      
      // Use requestAnimationFrame to maintain smooth UX and preserve focus
      requestAnimationFrame(() => {
      onFilter(newFilters);
      updateURLParams(newFilters); // Update URL when clearing search
      
        // Ensure search input maintains focus after clearing
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        
        // Use a shorter timeout for clearing since it's immediate
      setTimeout(() => setIsApplyingFilter(false), 100);
      });
      
      return newFilters;
    });
    
    // Update pending filters separately to keep them in sync
    setPendingFilters(currentPending => {
      if (currentPending.search === null) {
        return currentPending; // No change, prevent re-render
      }
      return {...currentPending, search: null};
    });
  }, [onFilter]);
  
  // Apply initial filter only once on mount - using ref to prevent reapplication
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      // Create a guaranteed default filter state
      const defaultFilter: FilterState = {
        ...initialFilterState,
        status: ['confirmed'],
        distance: "1000",
        sortDirection: 'desc'
      };
      
      // Override with initialFilters if provided
      const safeInitialFilters: Partial<FilterState> = initialFilters || {};
      const filterToApply = initialFilters ? {
        ...defaultFilter,
        ...safeInitialFilters,
        // Always ensure these critical values are set
        status: safeInitialFilters.status?.length ? safeInitialFilters.status : ['confirmed'],
        distance: safeInitialFilters.distance || "1000",
        sortDirection: safeInitialFilters.sortDirection || 'desc'
      } : defaultFilter;
      
      // Set our local state
      setFilters(filterToApply);
      setPendingFilters(filterToApply);
      
      // Apply to parent
      onFilter(filterToApply);
    }
  }, [onFilter, initialFilters]);
  
  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isAdvancedOpen && filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node) && !filterToggleRef.current?.contains(event.target as Node)) {
        setIsAdvancedOpen(false);
      }
    }
    
    // Only add the event listener if not on mobile
    if (!isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isAdvancedOpen, isMobile]);

  // Handle escape key to close panel
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Only handle escape if advanced filters are open AND we're the active element context
      if (event.key === 'Escape' && isAdvancedOpen && 
          (document.activeElement === document.body || 
           filterPanelRef.current?.contains(document.activeElement))) {
        event.stopPropagation(); // Prevent other listeners from interfering
        setIsAdvancedOpen(false);
      }
    }
    
    if (isAdvancedOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAdvancedOpen]);
  
  // Convert status to a human-readable label
  const getStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'confirmed': 'Confirmed',
      'likely': 'Likely',
      'ifclear': 'If Clear',
      'plausible': 'Plausible',
      'dubious': 'Dubious',
      'unlikely': 'Unlikely'
    };
    return statusLabels[status] || status;
  };
  
  // Get the appropriate color for a status
  const getStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      'confirmed': 'bg-green-500',
      'likely': 'bg-blue-500',
      'ifclear': 'bg-cyan-500',
      'plausible': 'bg-yellow-500',
      'dubious': 'bg-orange-500',
      'unlikely': 'bg-red-500'
    };
    return statusColors[status] || 'bg-gray-500';
  };
  
  // Normalize status values for consistent comparison
  const normalizeStatus = (status: string): string => {
    // Handle if-clear format conversion
    if (status === 'if-clear') return 'ifclear';
    return status;
  };
  
  // Normalize type values for consistent comparison
  const normalizeType = (type: string): string => {
    // Don't lowercase or modify - compare with the exact format from API
    return type.trim();
  };
  
  // Normalize continent values for consistent naming
  const normalizeContinent = (continent: string): string => {
    // Map Oceania to Australasia to maintain consistency
    if (continent === 'Oceania') {
      return 'Australasia';
    }
    return continent;
  };
  
  // Extract available filter options from views
  const {
    sourceStates,
    sourceCountries,
    sourceContinent,
    destinationStates,
    destinationCountries,
    destinationContinent,
    // Add maps to track relationships between locations
    countryToContinent,
    stateToCountry,
    // Add maps for reverse lookup
    continentToCountries,
    countryToStates
  } = useMemo(() => {
    const sourceStatesSet = new Set<string>();
    const sourceCountriesSet = new Set<string>();
    const sourceContinentSet = new Set<string>();
    const destinationStatesSet = new Set<string>();
    const destinationCountriesSet = new Set<string>();
    const destinationContinentSet = new Set<string>();
    
    // Create maps to track relationships
    const countryToContinent: Record<string, string> = {};
    const stateToCountry: Record<string, string> = {};
    
    // Create reverse lookup maps
    const continentToCountries: Record<string, string[]> = {};
    const countryToStates: Record<string, string[]> = {};
    
    for (const view of views) {
      // Source locations - normalize continents
      if (view.state) sourceStatesSet.add(view.state);
      if (view.country) sourceCountriesSet.add(view.country);
      if (view.continent) sourceContinentSet.add(normalizeContinent(view.continent));
      
      // Destination locations - normalize continents
      if (view.destinationState) destinationStatesSet.add(view.destinationState);
      if (view.destinationCountry) destinationCountriesSet.add(view.destinationCountry);
      if (view.destinationContinent) destinationContinentSet.add(normalizeContinent(view.destinationContinent));
      
      // Track relationships: country to continent (normalized)
      if (view.country && view.continent) {
        const normalizedContinent = normalizeContinent(view.continent);
        countryToContinent[view.country] = normalizedContinent;
        
        // Build reverse mapping: continent to countries
        if (!continentToCountries[normalizedContinent]) {
          continentToCountries[normalizedContinent] = [];
        }
        if (!continentToCountries[normalizedContinent].includes(view.country)) {
          continentToCountries[normalizedContinent].push(view.country);
        }
      }
      
      // Track relationships: state to country
      if (view.state && view.country) {
        stateToCountry[view.state] = view.country;
        
        // Build reverse mapping: country to states
        if (!countryToStates[view.country]) {
          countryToStates[view.country] = [];
        }
        if (!countryToStates[view.country].includes(view.state)) {
          countryToStates[view.country].push(view.state);
        }
      }
      
      // Destination relationships: country to continent (normalized)
      if (view.destinationCountry && view.destinationContinent) {
        const normalizedDestContinent = normalizeContinent(view.destinationContinent);
        countryToContinent[view.destinationCountry] = normalizedDestContinent;
        
        // Build reverse mapping for destination: continent to countries
        if (!continentToCountries[normalizedDestContinent]) {
          continentToCountries[normalizedDestContinent] = [];
        }
        if (!continentToCountries[normalizedDestContinent].includes(view.destinationCountry)) {
          continentToCountries[normalizedDestContinent].push(view.destinationCountry);
        }
      }
      
      // Destination relationships: state to country
      if (view.destinationState && view.destinationCountry) {
        stateToCountry[view.destinationState] = view.destinationCountry;
        
        // Build reverse mapping for destination: country to states
        if (!countryToStates[view.destinationCountry]) {
          countryToStates[view.destinationCountry] = [];
        }
        if (!countryToStates[view.destinationCountry].includes(view.destinationState)) {
          countryToStates[view.destinationCountry].push(view.destinationState);
        }
      }
    }
    
    return {
      sourceStates: Array.from(sourceStatesSet).sort(),
      sourceCountries: Array.from(sourceCountriesSet).sort(),
      sourceContinent: Array.from(sourceContinentSet).sort(),
      destinationStates: Array.from(destinationStatesSet).sort(),
      destinationCountries: Array.from(destinationCountriesSet).sort(),
      destinationContinent: Array.from(destinationContinentSet).sort(),
      countryToContinent,
      stateToCountry,
      continentToCountries,
      countryToStates
    };
  }, [views]);
  
  // Filter source locations based on search term AND current filter selections
  const filteredSourceLocations = useMemo(() => {
    const searchTerm = sourceSearchTerm.toLowerCase();
    
    // Filter continents by search
    const continents = sourceContinent.filter(continent => 
      !searchTerm || continent.toLowerCase().includes(searchTerm)
    );
    
    // Filter countries based on selected continents and search term
    const countries = sourceCountries.filter(country => {
      const matchesSearch = !searchTerm || country.toLowerCase().includes(searchTerm);
      const matchesContinent = pendingFilters.sourceContinent.length === 0 || 
        (countryToContinent[country] && pendingFilters.sourceContinent.includes(countryToContinent[country]));
      
      return matchesSearch && matchesContinent;
    });
    
    // Filter states based on selected countries and search term
    const states = sourceStates.filter(state => {
      const matchesSearch = !searchTerm || state.toLowerCase().includes(searchTerm);
      const countryOfState = stateToCountry[state];
      
      // Does this state belong to any of the selected countries?
      const matchesCountry = pendingFilters.sourceCountries.length === 0 || 
        (countryOfState && pendingFilters.sourceCountries.includes(countryOfState));
      
      // If we have continent filter but no country filter, check if state's country is in selected continent
      const matchesContinent = pendingFilters.sourceContinent.length === 0 || 
        (countryOfState && 
         countryToContinent[countryOfState] && 
         pendingFilters.sourceContinent.includes(countryToContinent[countryOfState]));
      
      return matchesSearch && (matchesCountry || pendingFilters.sourceCountries.length === 0) && matchesContinent;
    });
    
    return { continents, countries, states };
  }, [sourceSearchTerm, sourceStates, sourceCountries, sourceContinent, countryToContinent, stateToCountry, pendingFilters]);
  
  // Filter destination locations based on search term AND current filter selections
  const filteredDestLocations = useMemo(() => {
    const searchTerm = destSearchTerm.toLowerCase();
    
    // Filter continents by search
    const continents = destinationContinent.filter(continent => 
      !searchTerm || continent.toLowerCase().includes(searchTerm)
    );
    
    // Filter countries based on selected continents and search term
    const countries = destinationCountries.filter(country => {
      const matchesSearch = !searchTerm || country.toLowerCase().includes(searchTerm);
      const matchesContinent = pendingFilters.destinationContinent.length === 0 || 
        (countryToContinent[country] && pendingFilters.destinationContinent.includes(countryToContinent[country]));
      
      return matchesSearch && matchesContinent;
    });
    
    // Filter states based on selected countries and search term
    const states = destinationStates.filter(state => {
      const matchesSearch = !searchTerm || state.toLowerCase().includes(searchTerm);
      const countryOfState = stateToCountry[state];
      
      // Does this state belong to any of the selected countries?
      const matchesCountry = pendingFilters.destinationCountries.length === 0 || 
        (countryOfState && pendingFilters.destinationCountries.includes(countryOfState));
      
      // If we have continent filter but no country filter, check if state's country is in selected continent
      const matchesContinent = pendingFilters.destinationContinent.length === 0 || 
        (countryOfState && 
         countryToContinent[countryOfState] && 
         pendingFilters.destinationContinent.includes(countryToContinent[countryOfState]));
      
      return matchesSearch && (matchesCountry || pendingFilters.destinationCountries.length === 0) && matchesContinent;
    });
    
    return { continents, countries, states };
  }, [destSearchTerm, destinationStates, destinationCountries, destinationContinent, countryToContinent, stateToCountry, pendingFilters]);
  
  // Calculate distance range from views
  useEffect(() => {
    if (views.length > 0) {
      const distances = views.map(view => {
        return view.distanceKm;
      });
      
      const minDistance = Math.floor(Math.min(...distances));
      const maxDistance = Math.ceil(Math.max(...distances));
      setDistanceRange([minDistance, maxDistance]);
      
      // Initialize distance filter if not set
      if (!filters.distance) {
        setFilters(prev => ({
          ...prev,
          distance: "1000" // Use string format for consistency
        }));
      }
    }
  }, [views, filters.distance]);
  
  // Check if filters have changed from initial state
  useEffect(() => {
    const defaultStatus = initialFilterState.status || [];
    const currentStatus = filters.status || [];
    
    const isChanged = Boolean(
      (filters.search !== null && filters.search !== '') ||
      (currentStatus.length !== defaultStatus.length || 
       !currentStatus.every(status => defaultStatus.includes(status))) ||
      filters.sourceStates.length > 0 ||
      filters.sourceCountries.length > 0 ||
      filters.sourceContinent.length > 0 ||
      filters.destinationStates.length > 0 ||
      filters.destinationCountries.length > 0 ||
      filters.destinationContinent.length > 0 ||
      filters.category.length > 0 ||
      filters.seasons.length > 0 ||
      (filters.distance !== initialFilterState.distance) ||
      (filters.location && filters.location !== '')
    );

    setIsFilterChanged(isChanged);
  }, [
    filters.search,
    filters.status,
    filters.sourceStates,
    filters.sourceCountries,
    filters.sourceContinent,
    filters.destinationStates,
    filters.destinationCountries,
    filters.destinationContinent,
    filters.category,
    filters.seasons,
    filters.distance,
    filters.location
  ]);
  
  // Status filter click
  const handleStatusFilter = (status: string) => {
    const isSelected = filters.status?.includes(status) || false;
    let newStatus: string[];
    
    if (isSelected) {
      newStatus = filters.status?.filter(s => s !== status) || [];
      if (newStatus.length === 0) newStatus = ['confirmed']; // Default to confirmed if nothing selected
    } else {
      newStatus = [...(filters.status || [])];
      if (!newStatus.includes(status)) {
        newStatus.push(status);
      }
    }
    
    // Update pending filters only (advanced panel doesn't apply immediately)
    setPendingFilters({...pendingFilters, status: newStatus});
  };
  
  // Apply filter changes - this should ONLY update state, not apply filters
  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    // No automatic filter application - we only do this explicitly
  };
  
  // Function to explicitly apply current filters
  const applyCurrentFilters = () => {
    // Capture current filter state
    const currentFilters = {...filters};
    
    setIsApplyingFilter(true);
    const timer = setTimeout(() => {
      onFilter(currentFilters);
      setIsApplyingFilter(false);
    }, 300);
    
    return () => clearTimeout(timer);
  };
  
  // Helper function to check if a property is an array-type property
  const isArrayProperty = (key: keyof FilterState): boolean => {
    return [
      'status', 
      'sourceStates', 
      'sourceCountries', 
      'sourceContinent', 
      'destinationStates', 
      'destinationCountries', 
      'destinationContinent',
      'category',
      'seasons',
      'type'
    ].includes(key);
  };
  
  // Helper function to safely set filter values with proper typing
  const setFilterValue = <K extends keyof FilterState>(
    filters: FilterState, 
    key: K, 
    value: FilterState[K]
  ): FilterState => {
    return {
      ...filters,
      [key]: value,
    };
  };

  // Handle checkbox change in all array-type filters without filter application
  const handleCheckboxChange = (category: keyof FilterState, value: string) => {
    if (!isArrayProperty(category)) return;
    
    // For type category, we need exact matching 
    const normalizedValue = value.trim();
    
    const currentValues = pendingFilters[category] as string[];
    const newValues = currentValues.includes(normalizedValue)
      ? currentValues.filter(v => v !== normalizedValue)
      : [...currentValues, normalizedValue];
    
    // Create a new filter object with the updated values
    const newFilters = {
      ...pendingFilters, 
      [category]: newValues 
    };
    
    // Update pending filters
    setPendingFilters(newFilters);
    
    // If we're not in the advanced panel, apply the filter immediately
    if (!isAdvancedOpen) {
      setFilters(newFilters);
      setIsApplyingFilter(true);
      onFilter(newFilters);
      updateURLParams(newFilters); // Update URL for checkbox changes
      setIsApplyingFilter(false);
    }
  };
  
  // Handle checkbox change for cascading filters with updated approach
  const handleCascadingFilterChange = (category: keyof FilterState, value: string) => {
    // Only handle array-type properties
    if (!isArrayProperty(category)) return;
    
    // Start with current pending filters
    const currentValues = (pendingFilters[category] || []) as string[];
    
    // Create base update with toggled value
    let updatedFilters: FilterState;
    
    if (currentValues.includes(value)) {
      // Remove the value
      const newValues = currentValues.filter(v => v !== value);
      updatedFilters = {
        ...pendingFilters,
        [category]: newValues
      };
      
      // Handle cascading removal for continents -> countries -> states
      if (category === 'sourceContinent' || category === 'destinationContinent') {
        const countryCategory = category === 'sourceContinent' ? 'sourceCountries' : 'destinationCountries';
        const stateCategory = category === 'sourceContinent' ? 'sourceStates' : 'destinationStates';
        
        // Get countries in this continent
        const countriesInContinent = continentToCountries[value] || [];
        
        // Remove these countries from selected countries
        const currentCountries = (pendingFilters[countryCategory] || []) as string[];
        const updatedCountries = currentCountries.filter(country => !countriesInContinent.includes(country));
        updatedFilters[countryCategory] = updatedCountries;
        
        // Remove all states from these countries
        const currentStates = (pendingFilters[stateCategory] || []) as string[];
        let statesInCountries: string[] = [];
        
        // Collect all states that belong to the removed countries
        for (const country of countriesInContinent) {
          const states = countryToStates[country] || [];
          statesInCountries = [...statesInCountries, ...states];
        }
        
        // Filter out these states
        const updatedStates = currentStates.filter(state => !statesInCountries.includes(state));
        updatedFilters[stateCategory] = updatedStates;
      }
      
      // Handle cascading removal for countries -> states
      if (category === 'sourceCountries' || category === 'destinationCountries') {
        const stateCategory = category === 'sourceCountries' ? 'sourceStates' : 'destinationStates';
        
        // Get states in this country
        const statesInCountry = countryToStates[value] || [];
        
        // Remove these states from selected states
        const currentStates = (pendingFilters[stateCategory] || []) as string[];
        const updatedStates = currentStates.filter(state => !statesInCountry.includes(state));
        updatedFilters[stateCategory] = updatedStates;
      }
    } else {
      // Add the value
      const newValues = [...currentValues, value];
      updatedFilters = {
        ...pendingFilters,
        [category]: newValues
      };
      
      // Handle cascading addition
      if (category === 'sourceCountries' || category === 'destinationCountries') {
        // When adding a country, ensure the country's continent is selected if not already
        const continentCategory = category === 'sourceCountries' ? 'sourceContinent' : 'destinationContinent';
        const continent = countryToContinent[value];
        
        const currentContinents = (pendingFilters[continentCategory] || []) as string[];
        if (continent && !currentContinents.includes(continent)) {
          updatedFilters[continentCategory] = [...currentContinents, continent];
        }
      }
    }
    
    // Update pending filters
    setPendingFilters(updatedFilters);
    
    // If we're not in the advanced panel, apply the filter immediately
    if (!isAdvancedOpen) {
      setFilters(updatedFilters);
      setIsApplyingFilter(true);
      onFilter(updatedFilters);
      updateURLParams(updatedFilters); // Update URL for cascading changes
      setIsApplyingFilter(false);
    }
  };
  
  // Select all items in a cascading category with updated approach
  const selectAllCascading = (category: keyof FilterState, items: string[]) => {
    if (!isArrayProperty(category)) return;
    
    // Start with current pending filters and set values for this category
    const updatedFilters = {
      ...pendingFilters,
      [category]: items
    };
    
    // If selecting all continents, also select all their countries and states
    if (category === 'sourceContinent' || category === 'destinationContinent') {
      const countryCategory = category === 'sourceContinent' ? 'sourceCountries' : 'destinationCountries';
      const stateCategory = category === 'sourceContinent' ? 'sourceStates' : 'destinationStates';
      
      // Collect all countries from these continents
      let allCountries: string[] = [];
      for (const continent of items) {
        const countries = continentToCountries[continent] || [];
        allCountries = [...allCountries, ...countries];
      }
      
      // Set the countries
      updatedFilters[countryCategory] = allCountries;
      
      // Collect all states from these countries
      let allStates: string[] = [];
      for (const country of allCountries) {
        const states = countryToStates[country] || [];
        allStates = [...allStates, ...states];
      }
      
      // Set the states
      updatedFilters[stateCategory] = allStates;
    }
    
    // If selecting all countries, also select all their states
    if (category === 'sourceCountries' || category === 'destinationCountries') {
      const stateCategory = category === 'sourceCountries' ? 'sourceStates' : 'destinationStates';
      
      // Collect all states from these countries
      let allStates: string[] = [];
      for (const country of items) {
        const states = countryToStates[country] || [];
        allStates = [...allStates, ...states];
      }
      
      // Set the states
      updatedFilters[stateCategory] = allStates;
    }
    
    // Update pending filters
    setPendingFilters(updatedFilters);
    
    // If we're not in the advanced panel, apply the filter immediately
    if (!isAdvancedOpen) {
      setFilters(updatedFilters);
      setIsApplyingFilter(true);
      onFilter(updatedFilters);
      updateURLParams(updatedFilters); // Update URL for cascading changes
      setIsApplyingFilter(false);
    }
  };
  
  // Clear all items in a cascading category with updated approach
  const clearAllCascading = (category: keyof FilterState) => {
    if (!isArrayProperty(category)) return;
    
    // Start with current pending filters and clear this category
    const updatedFilters = {
      ...pendingFilters,
      [category]: []
    };
    
    // If clearing continents, we don't need to clear countries/states as they will be filtered out by the cascade
    // But if specifically clearing countries, we should clear states too
    if (category === 'sourceCountries' || category === 'destinationCountries') {
      const stateCategory = category === 'sourceCountries' ? 'sourceStates' : 'destinationStates';
      updatedFilters[stateCategory] = [];
    }
    
    // Also clear any related cascading selects when a continent is cleared
    if (category === 'sourceContinent') {
      updatedFilters.sourceCountries = [];
      updatedFilters.sourceStates = [];
    } else if (category === 'destinationContinent') {
      updatedFilters.destinationCountries = [];
      updatedFilters.destinationStates = [];
    }
    
    // Update pending filters
    setPendingFilters(updatedFilters);
    
    // If we're not in the advanced panel, apply the filter immediately
    if (!isAdvancedOpen) {
      setFilters(updatedFilters);
      setIsApplyingFilter(true);
      onFilter(updatedFilters);
      updateURLParams(updatedFilters); // Update URL for cascading changes
      setIsApplyingFilter(false);
    }
  };

  // Clear only location filters without variable reassignment
  const clearLocationFilters = (locationType: 'source' | 'destination' | 'all') => {
    // Create a new object with current pending filters
    const updatedFilters = { ...pendingFilters };
    
    if (locationType === 'source' || locationType === 'all') {
      updatedFilters.sourceStates = [];
      updatedFilters.sourceCountries = [];
      updatedFilters.sourceContinent = [];
    }
    
    if (locationType === 'destination' || locationType === 'all') {
      updatedFilters.destinationStates = [];
      updatedFilters.destinationCountries = [];
      updatedFilters.destinationContinent = [];
    }
    
    // Update pending filters
    setPendingFilters(updatedFilters);
    
    // If this is the "clear all" command or when coming from a button directly (not in advanced filter panel),
    // immediately apply the changes to the actual filters object as well
    if (locationType === 'all' || !isAdvancedOpen) {
      setFilters(updatedFilters);
      
      // Apply the filter changes immediately
      setIsApplyingFilter(true);
      onFilter(updatedFilters);
      setIsApplyingFilter(false);
    }
  };

  // Apply pending filters
  const applyPendingFilters = () => {
    // Update actual filters with pending filters
    setFilters(pendingFilters);
    
    // Apply the filter changes
    setIsApplyingFilter(true);
    const timer = setTimeout(() => {
      onFilter(pendingFilters);
      updateURLParams(pendingFilters); // Update URL with pending filters
      setIsApplyingFilter(false);
    }, 300);
    
    return () => clearTimeout(timer);
  };
  
  // Handle distance change
  const handleDistanceChange = (value: string) => {
    handleFilterChange({ ...filters, distance: value });
  };
  
  // Handle sort change
  const handleSortChange = (field: string) => {
    if (filters.sortBy === field) {
      // Toggle direction if same field
      handleFilterChange({
        ...filters,
        sortDirection: filters.sortDirection === 'asc' ? 'desc' : 'asc'
      });
    } else {
      // Set new field with default direction
      handleFilterChange({
        ...filters,
        sortBy: field,
        sortDirection: 'desc'
      });
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    const resetFilters = {
      ...initialFilterState,
      // Keep the search term if it exists
      search: filters.search,
      // Preserve sort settings
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection
    };
    
    // Explicitly set all location-related filters to empty arrays
    resetFilters.sourceStates = [];
    resetFilters.sourceCountries = [];
    resetFilters.sourceContinent = [];
    resetFilters.destinationStates = [];
    resetFilters.destinationCountries = [];
    resetFilters.destinationContinent = [];
    resetFilters.type = []; // Reset type filter
    
    // Apply the reset filters
    setFilters(resetFilters);
    
    // Also update pending filters to match
    setPendingFilters(resetFilters);
    
    // Clear any active filter ID
    setActiveFilterId(null);
    
    // Apply the filter changes immediately
    setIsApplyingFilter(true);
    const timer = setTimeout(() => {
      onFilter(resetFilters);
      updateURLParams(resetFilters); // Update URL when clearing filters
      setIsApplyingFilter(false);
    }, 300);
    
    return () => clearTimeout(timer);
  };
  
  // Save current filter
  const saveCurrentFilter = () => {
    if (!filterName.trim()) return;
    
    const newFilter: SavedFilter = {
      id: Date.now().toString(),
      name: filterName,
      filter: { ...filters },
      createdAt: new Date()
    };
    
    setSavedFilters(prev => [newFilter, ...prev]);
    setFilterName('');
    setIsSaveModalOpen(false);
    setActiveFilterId(newFilter.id);
  };
  
  // Load saved filter
  const loadSavedFilter = (filterId: string) => {
    const filterToLoad = savedFilters.find(f => f.id === filterId);
    if (filterToLoad) {
      handleFilterChange(filterToLoad.filter);
      setActiveFilterId(filterId);
    }
  };
  
  // Delete saved filter
  const deleteSavedFilter = (filterId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSavedFilters(prev => prev.filter(f => f.id !== filterId));
    if (activeFilterId === filterId) {
      setActiveFilterId(null);
    }
  };
  
  // Count selected items in a category
  const countSelected = (category: keyof FilterState) => {
    return (pendingFilters[category] as string[]).length;
  };
  
  // Check if skylines only filter is active
  const isSkylineOnly = useMemo(() => {
    return filters.type.length === 1 && filters.type[0] === 'Skyline';
  }, [filters.type]);
  
  // Toggle skylines only filter
  const toggleSkylineOnly = useCallback(() => {
    const newFilters = {
      ...filters,
      type: isSkylineOnly ? [] : ['Skyline']
    };
    
    setFilters(newFilters);
    setPendingFilters(newFilters);
    setIsApplyingFilter(true);
    onFilter(newFilters);
    updateURLParams(newFilters);
    setTimeout(() => setIsApplyingFilter(false), 100);
  }, [filters, isSkylineOnly, onFilter, updateURLParams]);

  return (
    <div className="mb-6 relative" ref={filterContainerRef}>
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search input - using controlled component pattern */}
        <div className="relative flex-grow min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search views..."
            value={inputValue}
            onChange={handleInputChange}
            onFocus={(e) => {
              // Prevent the input from losing focus during filter operations
              e.target.focus();
            }}
            onBlur={(e) => {
              // Only allow blur if it's intentional (clicking outside, tab, etc.)
              // But not if it's due to re-renders
              if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
                // This is an intentional blur, allow it
              } else {
                // This might be due to re-render, restore focus
                setTimeout(() => {
                  if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }, 0);
              }
            }}
            className="pl-10 pr-10 py-2 w-full border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            disabled={isLoading || isApplyingFilter}
            title="Search by name, location, or keywords"
            key="search-input"
            autoComplete="off"
            spellCheck="false"
          />
          {inputValue && (
            <button
              type="button" 
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isApplyingFilter && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          )}
        </div>
          
        {/* Quick filter buttons row - optimize layout */}
        <div className="flex flex-wrap items-center gap-1 md:gap-2">
          {/* Status filter chips */}
          <div className="grid grid-cols-3 gap-1 sm:flex sm:flex-wrap">
            {availableStatuses.map(status => {
              const isSelected = pendingFilters.status?.includes(status) || false;
              
              return (
                <button
                  key={status}
                  ref={status === 'confirmed' ? confirmedButtonRef : undefined}
                  type="button"
                  onClick={() => {
                    const currentStatus = pendingFilters.status || [];
                    let newStatus: string[];
                    
                    if (currentStatus.includes(status)) {
                      newStatus = currentStatus.filter(s => s !== status);
                      // Ensure at least one status is selected
                      if (newStatus.length === 0) newStatus = ['confirmed'];
                    } else {
                      newStatus = [...currentStatus, status];
                    }
                    
                    // Apply filters immediately for main UI status buttons (outside advanced panel)
                    setFilters(currentFilters => {
                      const newFilters = {...currentFilters, status: newStatus};
                      
                      // Apply the filter immediately
                      setIsApplyingFilter(true);
                      onFilter(newFilters);
                      updateURLParams(newFilters); // Update URL for status changes
                      setTimeout(() => setIsApplyingFilter(false), 100);
                      
                      return newFilters;
                    });

                    // Also update pending filters to keep them in sync
                    setPendingFilters(currentPending => ({...currentPending, status: newStatus}));
                  }}
                  className={`px-1.5 py-1.5 sm:px-2 sm:py-1 text-xs rounded-md transition-all duration-200 flex items-center gap-1 min-w-0 sm:min-w-[80px] justify-center min-h-[36px] sm:min-h-0 ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary border shadow-sm'
                      : 'border-muted/70 bg-background hover:bg-muted/30 text-muted-foreground hover:text-foreground border'
                  }`}
                  title={`Filter by ${getStatusLabel(status)} status`}
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${getStatusColor(status)}`} />
                  <span className={isSelected ? 'font-medium' : ''}>{getStatusLabel(status)}</span>
                </button>
              );
            })}
          </div>
          
          {/* Advanced filter button */}
          <button
            ref={filterToggleRef}
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 border rounded-md transition-all font-medium h-[38px]",
              isAdvancedOpen
                ? "bg-primary/15 text-primary border-primary/30 shadow-md"
                : "bg-gradient-to-r from-blue-100/80 to-blue-200/50 dark:from-blue-900/30 dark:to-blue-800/40 hover:from-blue-200/70 hover:to-blue-300/50 dark:hover:from-blue-800/40 dark:hover:to-blue-700/50 border-blue-300/70 dark:border-blue-600/50 hover:border-blue-400/80 dark:hover:border-blue-500/60 text-blue-700 dark:text-blue-200"
            )}
            disabled={isLoading || isApplyingFilter}
            title="Toggle advanced filters panel"
          >
            <Filter className={cn("h-4.5 w-4.5", isAdvancedOpen ? "text-primary" : "text-blue-600 dark:text-blue-300")} />
            <span className={cn("text-sm", isAdvancedOpen ? "text-primary" : "text-blue-800 dark:text-blue-200")}>Filters</span>
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isAdvancedOpen ? "text-primary rotate-180" : "text-blue-600 dark:text-blue-300")} />
          </button>

          {/* Skylines Only toggle button */}
          <button
            type="button"
            onClick={toggleSkylineOnly}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 border rounded-md transition-all font-medium h-[38px]",
              isSkylineOnly 
                ? "bg-gradient-to-r from-orange-500/90 to-amber-500/90 dark:from-orange-600/90 dark:to-amber-600/90 border-orange-400 dark:border-orange-500 text-white shadow-md" 
                : "bg-gradient-to-r from-orange-100/80 to-amber-200/50 dark:from-orange-900/30 dark:to-amber-800/40 hover:from-orange-200/70 hover:to-amber-300/50 dark:hover:from-orange-800/40 dark:hover:to-amber-700/50 border-orange-300/70 dark:border-orange-600/50 hover:border-orange-400/80 dark:hover:border-orange-500/60 text-orange-700 dark:text-orange-200"
            )}
            disabled={isLoading || isApplyingFilter}
            title={isSkylineOnly ? "Show all view types" : "Show only skyline views"}
          >
            <Building2 className={cn(
              "h-4.5 w-4.5", 
              isSkylineOnly 
                ? "text-white" 
                : "text-orange-600 dark:text-orange-300"
            )} />
            <span className={cn(
              "text-xs", 
              isSkylineOnly 
                ? "text-white" 
                : "text-orange-800 dark:text-orange-200"
            )}>
              {isSkylineOnly ? 'Skylines ✓' : 'Skylines Only'}
            </span>
          </button>

          {/* Random View button */}
          {onRandomView && (
            <button
              type="button"
              onClick={onRandomView}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 border rounded-md transition-all font-medium h-[38px]",
                "bg-gradient-to-r from-purple-100/80 to-indigo-200/50 dark:from-purple-900/30 dark:to-indigo-800/40 hover:from-purple-200/70 hover:to-indigo-300/50 dark:hover:from-purple-800/40 dark:hover:to-indigo-700/50 border-purple-300/70 dark:border-purple-600/50 hover:border-purple-400/80 dark:hover:border-purple-500/60 text-purple-700 dark:text-purple-200"
              )}
              disabled={isLoading || isApplyingFilter}
              title="View a random sight-line from the database"
            >
              <svg className="h-4.5 w-4.5 text-purple-600 dark:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-xs text-purple-800 dark:text-purple-200">Random View</span>
            </button>
          )}
            
          {isFilterChanged && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
              title="Reset all filters to default values"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Advanced filter panel */}
      {isAdvancedOpen && (
        <div
          ref={filterPanelRef}
          className={cn(
            "z-50 rounded-md border bg-background/95 backdrop-blur-md shadow-lg transition-all duration-200 animate-in fade-in slide-in-from-top-2",
            // Mobile: fixed full-screen overlay
            "fixed inset-0 mt-0 rounded-none sm:rounded-md",
            // Desktop: absolute dropdown
            "sm:absolute sm:left-0 sm:right-0 sm:top-full sm:mt-2 sm:inset-auto"
          )}
          style={{
            maxHeight: isMobile ? '100vh' : '85vh',
            overflowY: 'auto'
          }}
        >
          {/* Filter panel header - Added new section */}
          <div className="sticky top-0 z-10 bg-muted/80 backdrop-blur-md px-4 py-2 border-b flex items-center justify-between">
            <h2 className="text-sm font-medium">Advanced Filters</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                disabled={isLoading || isApplyingFilter}
              >
                <X className="h-3 w-3" />
                Reset All
              </button>
              <button 
                type="button"
                onClick={() => setIsAdvancedOpen(false)}
                className="p-1 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close filter panel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Main filter content */}
          <div className="p-3 bg-black">
            {/* Quick filter row for status and distance */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              {/* Status filter section */}
              <div className="bg-card p-3 rounded-md border border-border/50">
                <h3 className="text-xs font-medium mb-2 flex items-center">
                  <span className="h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-blue-500 mr-2" />
                  Status
                </h3>
                <div className="grid grid-cols-3 gap-1 sm:flex sm:flex-wrap">
                  {availableStatuses.map(status => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => {
                        const currentStatus = pendingFilters.status || [];
                        let newStatus: string[];

                        if (currentStatus.includes(status)) {
                          newStatus = currentStatus.filter(s => s !== status);
                          // Ensure at least one status is selected
                          if (newStatus.length === 0) newStatus = ['confirmed'];
                        } else {
                          newStatus = [...currentStatus, status];
                        }

                        // Update pending filters only (advanced panel doesn't apply immediately)
                        setPendingFilters({...pendingFilters, status: newStatus});
                      }}
                      className={`px-1.5 py-1.5 sm:px-2 sm:py-1 text-xs rounded-md transition-all flex items-center gap-1 min-w-0 sm:min-w-[80px] justify-center min-h-[36px] sm:min-h-0 ${
                        pendingFilters.status?.includes(status)
                          ? 'bg-primary/10 text-primary border border-primary/30 shadow-sm'
                          : 'border hover:bg-muted/20 border-muted/70'
                      }`}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full ${getStatusColor(status)}`} />
                      <span className={pendingFilters.status?.includes(status) ? 'font-medium' : ''}>{getStatusLabel(status)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Distance range filter */}
              <div className="bg-card p-3 rounded-md border border-border/50">
                <h3 className="text-xs font-medium mb-2 flex items-center">
                  <span className="h-2 w-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 mr-2" />
                  Distance (kilometers)
                  <span className="ml-auto text-primary font-medium">
                    {typeof pendingFilters.distance === 'string' ? pendingFilters.distance : "1000"}
                  </span>
                </h3>
                <div className="px-1 py-1">
                  {/* Range input with custom styling */}
                  <div className="relative pb-2">
                    <div className="absolute h-2 bg-muted rounded-lg w-full" />
                    <div 
                      className="absolute h-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500" 
                      style={{
                        width: `${(Number.parseInt(typeof pendingFilters.distance === 'string' ? pendingFilters.distance : "1000", 10) / 1000) * 100}%`,
                        maxWidth: '100%',
                        zIndex: 1
                      }}
                    />
                    <input 
                      type="range"
                      min="0"
                      max="1000"
                      step="10"
                      value={typeof pendingFilters.distance === 'string' ? pendingFilters.distance : "1000"}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setPendingFilters({...pendingFilters, distance: newValue});
                      }}
                      className="slider-with-logo w-full accent-primary h-2 rounded-lg appearance-none cursor-pointer bg-transparent focus:outline-none absolute top-0 left-0 z-10"
                      disabled={isLoading || isApplyingFilter}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-3">
                    <span>0</span>
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-sm flex items-center">
                      <div className="h-1.5 w-1.5 rounded-full bg-white/80 mr-1.5 animate-pulse" />
                      {typeof pendingFilters.distance === 'string' ? pendingFilters.distance : "1000"} km
                    </span>
                    <span>1000</span>
                  </div>
                  
                  {/* Quick distance presets */}
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    {['100', '250', '500', '1000'].map(preset => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          const newFilters = {...pendingFilters, distance: preset};
                          setPendingFilters(newFilters);
                          
                          // If we're not in the advanced panel, apply the filter immediately
                          if (!isAdvancedOpen) {
                            setFilters(newFilters);
                            setIsApplyingFilter(true);
                            onFilter(newFilters);
                            setIsApplyingFilter(false);
                          }
                        }}
                        className={cn(
                          "px-2 py-1 text-xs rounded-md border transition-all",
                          pendingFilters.distance === preset
                            ? "bg-primary/10 border-primary/30 text-primary font-medium"
                            : "border-input hover:bg-muted/20"
                        )}
                        disabled={isLoading || isApplyingFilter}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Type filter section */}
              <div className="bg-card p-3 rounded-md border border-border/50">
                <h3 className="text-xs font-medium mb-2 flex items-center">
                  <span className="h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mr-2" />
                  Type
                  <span className="ml-auto text-primary font-medium">
                    {pendingFilters.type.length > 0 ? `${pendingFilters.type.length} selected` : 'All'}
                  </span>
                </h3>
                
                {/* Matching Mode Toggle */}
                <div className="mb-2 flex justify-between items-center text-xs border-b border-border/50 pb-2">
                  <span className="text-muted-foreground">Match mode:</span>
                  <div className="flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => {
                        const newFilters = { ...pendingFilters, exactTypeMatch: true };
                        setPendingFilters(newFilters);
                        if (!isAdvancedOpen) {
                          setFilters(newFilters);
                          onFilter(newFilters);
                        }
                      }}
                      className={`px-2 py-1 rounded text-xs ${pendingFilters.exactTypeMatch ? 
                        'bg-primary/20 text-primary font-medium border border-primary/30' : 
                        'hover:bg-muted/20'}`}
                    >
                      Exact
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const newFilters = { ...pendingFilters, exactTypeMatch: false };
                        setPendingFilters(newFilters);
                        if (!isAdvancedOpen) {
                          setFilters(newFilters);
                          onFilter(newFilters);
                        }
                      }}
                      className={`px-2 py-1 rounded text-xs ${!pendingFilters.exactTypeMatch ? 
                        'bg-primary/20 text-primary font-medium border border-primary/30' : 
                        'hover:bg-muted/20'}`}
                    >
                      Partial
                    </button>
                  </div>
                </div>
                
                <div className="max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                  <div className="flex flex-wrap gap-1">
                    {availableTypes.map((type) => {
                      // Check if this type is included in the filters using exact match
                      const isSelected = pendingFilters.type.includes(type);
                      
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleCheckboxChange('type', type)}
                          className={`px-2 py-1 text-xs rounded-md transition-all flex items-center gap-1 ${
                            isSelected
                              ? 'bg-primary/10 text-primary border border-primary/30 shadow-sm' 
                              : 'border hover:bg-muted/20 border-muted/70'
                          }`}
                        >
                          <span className={isSelected ? 'font-medium' : ''}>
                            {type}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {pendingFilters.type.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newFilters = {...pendingFilters, type: []};
                        setPendingFilters(newFilters);
                        
                        // If not in advanced panel, apply immediately
                        if (!isAdvancedOpen) {
                          setFilters(newFilters);
                          onFilter(newFilters);
                        }
                      }}
                      className="mt-2 text-xs text-primary hover:underline w-full text-right"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>

            </div>
          
            {/* Source and Destination section in tabs */}
            <div className="border rounded-md mt-4 bg-card overflow-hidden">
              <div className="px-4 py-2 bg-muted/40 border-b flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium">Location Filters</h3>
                </div>
                <button
                  type="button"
                  onClick={() => clearLocationFilters('all')}
                  className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted/30"
                >
                  <X className="h-3 w-3" />
                  Clear All Locations
                </button>
              </div>
              <div className="flex border-b">
                <button
                  type="button"
                  onClick={() => setLocationTab('source')}
                  className={cn(
                    "group flex-1 px-4 py-2.5 text-sm font-medium border-r flex items-center justify-center gap-2 transition-all relative",
                    locationTab === 'source'
                      ? "bg-primary/15 text-primary shadow-inner font-medium"
                      : "text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                  )}
                >
                  <MapPin className={cn(
                    "h-4 w-4", 
                    locationTab === 'source' ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                  )} />
                  Source Location
                  
                  {locationTab === 'source' && (
                    <>
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary shadow-[0_0_5px_1px_rgba(var(--primary),.3)]" />
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/4 h-0.5 bg-primary shadow-[0_0_8px_2px_rgba(var(--primary),.6)] animate-pulse" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setLocationTab('destination')}
                  className={cn(
                    "group flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-all relative",
                    locationTab === 'destination'
                      ? "bg-primary/15 text-primary shadow-inner font-medium"
                      : "text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                  )}
                >
                  <Mountain className={cn(
                    "h-4 w-4", 
                    locationTab === 'destination' ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                  )} />
                  Destination Location
                  
                  {locationTab === 'destination' && (
                    <>
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary shadow-[0_0_5px_1px_rgba(var(--primary),.3)]" />
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/4 h-0.5 bg-primary shadow-[0_0_8px_2px_rgba(var(--primary),.6)] animate-pulse" />
                    </>
                  )}
                </button>
              </div>
              
              <div className="p-3">
                {/* Location search */}
                <div className="mb-3 relative flex items-center">
                  <div className="relative flex-grow">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder={`Search ${locationTab === 'source' ? 'source' : 'destination'} continents, countries, states/provinces...`}
                      value={locationTab === 'source' ? sourceSearchTerm : destSearchTerm}
                      onChange={(e) => locationTab === 'source' 
                        ? setSourceSearchTerm(e.target.value)
                        : setDestSearchTerm(e.target.value)
                      }
                      className="w-full text-sm pl-10 pr-3 py-2 border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                    {(locationTab === 'source' ? sourceSearchTerm : destSearchTerm) && (
                      <button 
                        type="button"
                        onClick={() => locationTab === 'source' 
                          ? setSourceSearchTerm('')
                          : setDestSearchTerm('')
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => clearLocationFilters(locationTab === 'source' ? 'source' : 'destination')}
                    className="ml-2 px-2 py-1.5 text-xs border rounded-md hover:bg-muted/20 transition-colors flex items-center gap-1"
                  >
                    <X className="h-3 w-3" />
                    Clear {locationTab === 'source' ? 'Source' : 'Destination'}
                  </button>
                </div>
                
                {/* Location filters */}
                {locationTab === 'source' ? (
                  /* Source location filters */
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Source Continents */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center">
                          <Globe className="h-3 w-3 mr-1 text-blue-500" />
                          Continents
                        </h4>
                        <div className="flex items-center gap-1 text-[10px]">
                          <button type="button" onClick={() => selectAllCascading('sourceContinent', filteredSourceLocations.continents)}
                            className="text-primary hover:underline">All</button>
                          <span className="text-muted-foreground px-1">|</span>
                          <button type="button" onClick={() => clearAllCascading('sourceContinent')}
                            className="text-primary hover:underline">Clear</button>
                          <span className="ml-1 bg-primary/10 text-primary px-1 rounded-full text-[10px]">
                            {countSelected('sourceContinent')}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1.5 bg-background/50">
                        {filteredSourceLocations.continents.length === 0 ? (
                          <div className="text-xs text-muted-foreground text-center py-2">No results</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-0.5">
                            {filteredSourceLocations.continents.map(continent => (
                              <label key={continent} className="flex items-center text-xs hover:bg-muted/20 p-1 rounded">
                                <input type="checkbox" checked={pendingFilters.sourceContinent.includes(continent)}
                                  onChange={(e) => handleCascadingFilterChange('sourceContinent', continent)}
                                  className="mr-1.5 h-3 w-3 accent-primary" />
                                <span className="truncate">{continent}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Source Countries */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center">
                          <Globe className="h-3 w-3 mr-1 text-blue-500" />
                          Countries
                        </h4>
                        <div className="flex items-center gap-1 text-[10px]">
                          <button type="button" onClick={() => selectAllCascading('sourceCountries', filteredSourceLocations.countries)}
                            className="text-primary hover:underline">All</button>
                          <span className="text-muted-foreground px-1">|</span>
                          <button type="button" onClick={() => clearAllCascading('sourceCountries')}
                            className="text-primary hover:underline">Clear</button>
                          <span className="ml-1 bg-primary/10 text-primary px-1 rounded-full text-[10px]">
                            {countSelected('sourceCountries')}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1.5 bg-background/50">
                        {filteredSourceLocations.countries.length === 0 ? (
                          <div className="text-xs text-muted-foreground text-center py-2">No results</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-0.5">
                            {filteredSourceLocations.countries.map(country => (
                              <label key={country} className="flex items-center text-xs hover:bg-muted/20 p-1 rounded">
                                <input type="checkbox" checked={pendingFilters.sourceCountries.includes(country)}
                                  onChange={(e) => handleCascadingFilterChange('sourceCountries', country)}
                                  className="mr-1.5 h-3 w-3 accent-primary" />
                                <span className="truncate">{country}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Source States */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-blue-500" />
                          States/Provinces
                        </h4>
                        <div className="flex items-center gap-1 text-[10px]">
                          <button type="button" onClick={() => selectAllCascading('sourceStates', filteredSourceLocations.states)}
                            className="text-primary hover:underline">All</button>
                          <span className="text-muted-foreground px-1">|</span>
                          <button type="button" onClick={() => clearAllCascading('sourceStates')} 
                            className="text-primary hover:underline">Clear</button>
                          <span className="ml-1 bg-primary/10 text-primary px-1 rounded-full text-[10px]">
                            {countSelected('sourceStates')}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1.5 bg-background/50">
                        {filteredSourceLocations.states.length === 0 ? (
                          <div className="text-xs text-muted-foreground text-center py-2">No results</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-0.5">
                            {filteredSourceLocations.states.map(state => (
                              <label key={state} className="flex items-center text-xs hover:bg-muted/20 p-1 rounded">
                                <input type="checkbox" checked={pendingFilters.sourceStates.includes(state)}
                                  onChange={(e) => handleCascadingFilterChange('sourceStates', state)}
                                  className="mr-1.5 h-3 w-3 accent-primary" />
                                <span className="truncate">{state}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Destination location filters */
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Destination Continents */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center">
                          <Globe className="h-3 w-3 mr-1 text-green-500" />
                          Continents
                        </h4>
                        <div className="flex items-center gap-1 text-[10px]">
                          <button type="button" onClick={() => selectAllCascading('destinationContinent', filteredDestLocations.continents)}
                            className="text-primary hover:underline">All</button>
                          <span className="text-muted-foreground px-1">|</span>
                          <button type="button" onClick={() => clearAllCascading('destinationContinent')}
                            className="text-primary hover:underline">Clear</button>
                          <span className="ml-1 bg-primary/10 text-primary px-1 rounded-full text-[10px]">
                            {countSelected('destinationContinent')}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1.5 bg-background/50">
                        {filteredDestLocations.continents.length === 0 ? (
                          <div className="text-xs text-muted-foreground text-center py-2">No results</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-0.5">
                            {filteredDestLocations.continents.map(continent => (
                              <label key={continent} className="flex items-center text-xs hover:bg-muted/20 p-1 rounded">
                                <input type="checkbox" checked={pendingFilters.destinationContinent.includes(continent)}
                                  onChange={(e) => handleCascadingFilterChange('destinationContinent', continent)}
                                  className="mr-1.5 h-3 w-3 accent-primary" />
                                <span className="truncate">{continent}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Destination Countries */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center">
                          <Globe className="h-3 w-3 mr-1 text-green-500" />
                          Countries
                        </h4>
                        <div className="flex items-center gap-1 text-[10px]">
                          <button type="button" onClick={() => selectAllCascading('destinationCountries', filteredDestLocations.countries)}
                            className="text-primary hover:underline">All</button>
                          <span className="text-muted-foreground px-1">|</span>
                          <button type="button" onClick={() => clearAllCascading('destinationCountries')}
                            className="text-primary hover:underline">Clear</button>
                          <span className="ml-1 bg-primary/10 text-primary px-1 rounded-full text-[10px]">
                            {countSelected('destinationCountries')}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1.5 bg-background/50">
                        {filteredDestLocations.countries.length === 0 ? (
                          <div className="text-xs text-muted-foreground text-center py-2">No results</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-0.5">
                            {filteredDestLocations.countries.map(country => (
                              <label key={country} className="flex items-center text-xs hover:bg-muted/20 p-1 rounded">
                                <input type="checkbox" checked={pendingFilters.destinationCountries.includes(country)}
                                  onChange={(e) => handleCascadingFilterChange('destinationCountries', country)}
                                  className="mr-1.5 h-3 w-3 accent-primary" />
                                <span className="truncate">{country}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Destination States */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-xs font-medium text-muted-foreground flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-green-500" />
                          States/Provinces
                        </h4>
                        <div className="flex items-center gap-1 text-[10px]">
                          <button type="button" onClick={() => selectAllCascading('destinationStates', filteredDestLocations.states)}
                            className="text-primary hover:underline">All</button>
                          <span className="text-muted-foreground px-1">|</span>
                          <button type="button" onClick={() => clearAllCascading('destinationStates')}
                            className="text-primary hover:underline">Clear</button>
                          <span className="ml-1 bg-primary/10 text-primary px-1 rounded-full text-[10px]">
                            {countSelected('destinationStates')}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 max-h-32 overflow-y-auto border rounded p-1.5 bg-background/50">
                        {filteredDestLocations.states.length === 0 ? (
                          <div className="text-xs text-muted-foreground text-center py-2">No results</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-0.5">
                            {filteredDestLocations.states.map(state => (
                              <label key={state} className="flex items-center text-xs hover:bg-muted/20 p-1 rounded">
                                <input type="checkbox" checked={pendingFilters.destinationStates.includes(state)}
                                  onChange={(e) => handleCascadingFilterChange('destinationStates', state)}
                                  className="mr-1.5 h-3 w-3 accent-primary" />
                                <span className="truncate">{state}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Action buttons - sticky footer */}
          <div className="sticky bottom-0 left-0 right-0 bg-muted/90 backdrop-blur-sm border-t p-3 flex justify-end gap-2 z-10">
            <button
              type="button"
              onClick={() => {
                // Cancel by resetting pending filters to current applied filters
                setPendingFilters(filters);
                setIsAdvancedOpen(false);
              }}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors border rounded"
              disabled={isLoading || isApplyingFilter}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setIsAdvancedOpen(false);
                applyPendingFilters();
              }}
              className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30 flex items-center gap-2 shadow-sm transition-all"
              disabled={isLoading || isApplyingFilter}
            >
              {(isLoading || isApplyingFilter) && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              Apply and Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
